import React, { useState } from 'react';
import { useSystemInfo } from '../hooks/useSystemInfo';

/**
 * Renders the brand logo. In production it shows /logo.png. In demo mode it
 * shows a neutral inline SVG monogram so the real client's brand is never
 * exposed to demo visitors.
 *
 * Props:
 *   size      — number of CSS pixels for the square box (default 32)
 *   className — extra Tailwind classes
 *   alt       — alt text (defaults to brand name from system info)
 */
const BrandLogo = ({ size = 32, className = '', alt }) => {
  const { brand_name, use_generic_logo, logo_url } = useSystemInfo();
  const [broken, setBroken] = useState(false);
  const label = alt || brand_name;

  if (use_generic_logo) {
    // Neutral inline SVG — no client brand exposed.
    return (
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        role="img"
        aria-label={label}
        className={`object-contain flex-shrink-0 ${className}`}
      >
        <rect x="2" y="2" width="60" height="60" rx="10" fill="#0c1a2e" />
        <path
          d="M14 44 L24 20 L34 44 M18 36 H30"
          stroke="#fff"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M40 20 H50 M40 20 V44 M40 32 H48 M40 44 H50"
          stroke="#fff"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    );
  }

  // Active company has provided a custom logo URL — use it directly.
  // Falls back to /logo.png (the original 360 Carexport asset) when blank,
  // and gracefully degrades to a monogram if the URL is broken.
  const initials = (brand_name || '??').slice(0, 2).toUpperCase();
  const src = logo_url || '/logo.png';
  if (broken) {
    return (
      <div
        style={{ width: size, height: size }}
        className={`flex-shrink-0 flex items-center justify-center rounded bg-slate-900 text-white text-[10px] font-bold ${className}`}
        aria-label={label}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={label}
      style={{ width: size, height: size }}
      className={`object-contain flex-shrink-0 ${className}`}
      onError={() => setBroken(true)}
    />
  );
};

export default BrandLogo;
